import type { PartialProcedure } from '@/components/consent/useConsentForm'
import type { PaginationResponse, RequiredPaginationType } from '@/validators/pagination'
import type {
  CreateEducationalMaterialSchema,
  EducationalMaterial,
  GetEducationalMaterialsByCategoryIdSchema,
  GetEducationalMaterialsByProcedureIdResponseType,
  GetSearchResultForProcedureNamesSchema,
  Procedure,
  SearchEducationalMaterialByProcedureNameSchema,
  SupportedLanguage,
  UpdateEducationalMaterialSchema,
} from '@/validators/procedure'
import type { InferInput } from 'valibot'
import { apiClient } from '@/api/base'
import { useGetAllCategoriesQuery } from '@/api/category'
import { queryClient } from '@/api/provider'
import { useLanguage } from '@/components/home/language'
import { assetTypes, supportedLanguages } from '@/validators/procedure'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { object, optional, picklist, string } from 'valibot'

const EDUCATIONAL_MATERIAL_LIST_QUERY_KEY = 'educational-material-list'

function invalidateEducationalMaterialListQueries(props?: { procedureId?: unknown }) {
  queryClient.invalidateQueries({ queryKey: ['procedure', ...(props?.procedureId ? [props.procedureId] : [])] })
}

export function useGetAllEducationalMaterialsUnauthenticatedQuery(props: RequiredPaginationType) {
  return useQuery<PaginationResponse<EducationalMaterial & { procedure: Procedure }>>({
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryKey: ['unauthenticated-educational-material', props],
    queryFn: async () => {
      const res = await apiClient('/api/editor/ed-materials', {
        params: props,
        requireAuth: false,
      })
      return res.data
    },
  })
}

export function useGetSearchEducationalMaterialsQuery() {
  return useMutation({
    mutationKey: ['search-educational-material-by-procedure-name'],
    mutationFn: async (data: SearchEducationalMaterialByProcedureNameSchema) => {
      const res = await apiClient('/api/editor/search-ed-materials', {
        data,
        method: 'POST',
        requireAuth: false,
      })
      return res.data.data
    },
  })
}

export function useGetSearchEducationalMaterialsByTitleQuery() {
  return useMutation({
    mutationKey: ['search-educational-material-by-title'],
    mutationFn: async (data: GetSearchResultForProcedureNamesSchema) => {
      const res = await apiClient('/api/editor/search-ed-materials-by-title', { data, method: 'POST' })
      return res.data.data
    },
  })
}

export function useGetSingleEducationalMaterialByIdQuery(props: { educationalMaterialId: string }) {
  return useQuery<{ data: EducationalMaterial & { procedure: PartialProcedure }, others: EducationalMaterial[] }>({
    staleTime: 1000 * 60 * 5, // 5 minutes

    queryKey: ['educational-material-by-id', props.educationalMaterialId],
    queryFn: async () => {
      const { data } = await apiClient(
        `/api/editor/ed-materials/${props.educationalMaterialId}`,
        { requireAuth: false },
      )
      return data
    },
  })
}

export const getSingleEducationalMaterialByQueryParamsSchema = object({
  language: optional(picklist(supportedLanguages, 'Unsupported language'), 'english'),
  type: optional(picklist(assetTypes, 'Unsupported asset type'), 'blog'),
  procedureId: string('Procedure ID is required'),
})
type GetSingleEducationalMaterialByQueryParamsSchema =
InferInput<typeof getSingleEducationalMaterialByQueryParamsSchema>
export function useGetSingleEducationalMaterialByQueryParams(props: GetSingleEducationalMaterialByQueryParamsSchema) {
  return useQuery<{ data: EducationalMaterial & { procedure: PartialProcedure }, others: EducationalMaterial[] }>({
    queryKey: ['educational-material-by-query-params', props],
    queryFn: async () => {
      const { data } = await apiClient('/api/editor/ed-materials-params', {
        requireAuth: false,
        params: props,
      })
      return data
    },
  })
}

export function useCreateEducationalMaterialMutation() {
  return useMutation({
    mutationKey: [EDUCATIONAL_MATERIAL_LIST_QUERY_KEY],
    mutationFn: async (data: CreateEducationalMaterialSchema) => {
      const res = await apiClient('/api/editor/create-ed-material', { method: 'POST', data })
      invalidateEducationalMaterialListQueries({ procedureId: data.procedureId })
      return res.data
    },
  })
}

export async function updateEducationalMaterialFn(data: UpdateEducationalMaterialSchema) {
  const res = await apiClient('/api/editor/update-ed-material', { method: 'POST', data })
  invalidateEducationalMaterialListQueries({ procedureId: data.procedureId })
  queryClient.invalidateQueries({ queryKey: [data.educationalMaterialId] })
  return res.data
}

export function useUpdateEducationalMaterialMutation() {
  return useMutation({
    mutationFn: updateEducationalMaterialFn,
    mutationKey: [EDUCATIONAL_MATERIAL_LIST_QUERY_KEY],
  })
}

export function usePublishEducationalMaterialMutation() {
  return useMutation({
    mutationKey: [EDUCATIONAL_MATERIAL_LIST_QUERY_KEY],
    mutationFn: async (data: UpdateEducationalMaterialSchema) => {
      const res = await apiClient('/api/editor/publish-ed-material', { method: 'POST', data })
      invalidateEducationalMaterialListQueries({ procedureId: data.procedureId })
      return res.data
    },
  })
}

export function useUnPublishEducationalMaterialMutation() {
  return useMutation({
    mutationKey: [EDUCATIONAL_MATERIAL_LIST_QUERY_KEY],
    mutationFn: async (data: UpdateEducationalMaterialSchema) => {
      const res = await apiClient('/api/editor/unpublish-ed-material', { method: 'POST', data })
      invalidateEducationalMaterialListQueries({ procedureId: data.procedureId })
      return res.data
    },
  })
}

function useGetEducationalMaterialsByCategoryIdMutation() {
  return useMutation({
    mutationKey: ['educational-materials-by-category-id'],
    mutationFn: async (props: GetEducationalMaterialsByCategoryIdSchema) => {
      const res = await apiClient('/api/editor/ed-materials-by-category', {
        method: 'POST',
        requireAuth: false,
        data: props,
      })
      return res.data.data as { materials: GetEducationalMaterialsByProcedureIdResponseType, hasNextPage: boolean }
    },
  })
}

export function useGetEducationalMaterialsByProcedureIdQuery(
  props: { procedureId?: string, language?: SupportedLanguage },
) {
  return useQuery<GetEducationalMaterialsByProcedureIdResponseType>({
    queryKey: ['educational-materials-by-procedure-id', props.procedureId, props.language],
    queryFn: async () => {
      if (!props.procedureId || !props.language) return []
      const res = await apiClient('/api/editor/ed-materials-by-procedure', {
        method: 'POST',
        requireAuth: false,
        data: { procedureId: props.procedureId, language: props.language },
      })
      return res.data.data
    },
  })
}

function sortEducationalMaterialsByAssetType(materials: Omit<EducationalMaterial, 'procedure'>[]) {
  const uniqueMap = new Map<string, Omit<EducationalMaterial, 'procedure'>>()
  materials.forEach((material) => {
    uniqueMap.set(material._id as unknown as string, material)
  })
  return Array.from(uniqueMap.values()).sort((a, b) => {
    if (a.assetType === 'video') return -1
    if (b.assetType === 'video') return 1
    if (a.assetType === 'blog') return -1
    if (b.assetType === 'blog') return 1
    if (a.assetType === 'poster') return -1
    if (b.assetType === 'poster') return 1
    if (a.assetType === 'pdf') return -1
    if (b.assetType === 'pdf') return 1
    return 0
  })
}

function structureEducationalMaterialsByProcedureId(
  data: GetEducationalMaterialsByProcedureIdResponseType,
  prev: GetEducationalMaterialsByProcedureIdResponseType | null,
) {
  const allKeys = new Set([...Object.keys(data), ...Object.keys(prev || {})])

  return Array.from(allKeys).reduce<
    GetEducationalMaterialsByProcedureIdResponseType
  >((acc, key) => {
    return {
      ...acc,
      [key]: {
        ...(data[key] || {}),
        ...((prev || {})[key] || {}),
        educationalMaterials: sortEducationalMaterialsByAssetType([
          ...(data[key]?.educationalMaterials || []),
          ...((prev || {})[key]?.educationalMaterials || []),
        ]),
      },
    }
  }, prev || {})
}

export function useGetEducationalMaterials() {
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const { language } = useLanguage()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [educationalMaterials, setEducationalMaterials] = useState<
GetEducationalMaterialsByProcedureIdResponseType | null
  >(null)

  const prevValues = useRef<{ page: number, selectedCategory: string }>({ page, selectedCategory })

  const { data: categories, isPending: isCategoriesPending } = useGetAllCategoriesQuery()

  const {
    isPending: isEducationalMaterialsPending,
    mutateAsync: getEducationalMaterials,
  } = useGetEducationalMaterialsByCategoryIdMutation()

  const changeSelectedCategory = (category: string) => {
    setSelectedCategory(category)
    setPage(0)
  }

  function getData(props: { merge: boolean }) {
    getEducationalMaterials({ categoryId: selectedCategory as unknown as any, page, language })
      .then((res) => {
        setHasNextPage(res.hasNextPage)
        setEducationalMaterials(
          structureEducationalMaterialsByProcedureId(res.materials, props.merge ? educationalMaterials : null),
        )
      })
  }

  useEffect(() => {
    getData({ merge: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language])

  useEffect(() => {
    if (prevValues.current.selectedCategory === selectedCategory) return
    prevValues.current.selectedCategory = selectedCategory
    setEducationalMaterials(null)
    getData({ merge: false })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory])

  useEffect(() => {
    if (prevValues.current.page === page) return
    prevValues.current.page = page
    getData({ merge: page !== 0 })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  return {
    categories,
    hasNextPage,
    isCategoriesPending,
    selectedCategory,
    changeSelectedCategory,

    setPage,
    educationalMaterials,
    isEducationalMaterialsPending,
  }
}
